/**
 * One row on the "Meus pedidos" list — the design brief's Home screen card.
 *
 * The store name is deliberately always on the card (never a header/filter):
 * the same customer has orders from different franchises, and "your item is
 * ready" is useless without "at which store" (CLAUDE.md §2.4).
 */
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { StatusChip } from './StatusChip.tsx';
import { formatDate, formatMoney } from '../i18n/format.ts';
import { buildPath } from '../i18n/routes.ts';
import { currentLocale } from '../i18n/index.ts';
import type { ServiceOrderListItem } from '../api/serviceOrders.ts';
import styles from './ServiceOrderCard.module.css';
import { MONEY_VISIBLE } from '../money-visibility.ts';

interface Props {
  order: ServiceOrderListItem;
}

export function ServiceOrderCard({ order }: Props): React.JSX.Element {
  const { t } = useTranslation();
  const locale = currentLocale();
  const extraItems = order.items_count - 1;
  // Partial delivery only — a fully delivered order already says so via the
  // status chip, and repeating "3 de 3" next to "Entregue" is noise.
  const partiallyDelivered = order.delivered_items_count > 0 && order.delivered_items_count < order.items_count;

  return (
    <li className={styles['item']}>
      <Link
        className={styles['card']}
        to={buildPath('ordemServicoDetalhe', locale, { osId: String(order.order_id) })}
      >
        <div className={styles['top']}>
          <span className={styles['number']}>#{order.order_id}</span>
          <StatusChip status={order.status} />
        </div>

        <p className={styles['franchise']}>{order.franchise.name}</p>

        <p className={styles['description']}>
          {order.first_item_description}
          {extraItems > 0 && (
            <span className={styles['more']}>
              {' '}
              {t('order.itemSummaryMore', { count: extraItems })}
            </span>
          )}
        </p>

        <div className={styles['meta']}>
          <span>{t('order.openedOn', { date: formatDate(order.created_at) })}</span>
          {order.expected_delivery_at && (
            <span>{t('order.expectedDelivery', { date: formatDate(order.expected_delivery_at) })}</span>
          )}
        </div>

        {partiallyDelivered && (
          <p className={styles['partial']}>
            {t('order.itemsDelivered', { delivered: order.delivered_items_count, total: order.items_count })}
          </p>
        )}

        {/* Hidden while the payload's `currency` is a hard-coded literal —
            see money-visibility.ts. Not deleted: restoring it must be a flag
            flip, not a rewrite. */}
        {MONEY_VISIBLE && (
          <p className={styles['total']}>{formatMoney({ amount: order.total_cents, currency: order.currency })}</p>
        )}
      </Link>
    </li>
  );
}
